import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, catchError, finalize, forkJoin, map, of, switchMap, takeUntil, tap, throwError } from 'rxjs';
import { LongevityAssessmentModalComponent } from '../../requests-handling/request-round/longevity-request-assessments/longevity-assessment-modal.component';
import { ToastService } from '../../shared/services/toast-service/toast.service';
import { LongevityAlertsService } from './longevity-alerts.service';
import {
  MANAGER_CASE_STATUS_LABELS,
  LongevityAssessmentDetail,
  LongevityManagerCase,
  LongevityOperatorTarget,
  ManagerCaseStatus,
} from './longevity-alerts.model';
import {
  EMPTY_LONGEVITY_CASE_FILTERS,
  LongevityCaseFilters,
  LongevityCaseGroup,
  LongevityCaseSort,
  activeFilterCount,
  filterCases,
  groupCasesByOperator,
  sortCases,
} from './longevity-alerts.utils';

/**
 * Tab "Longevity" della pagina Avvisi: la coda dei casi che hanno superato una soglia
 * di attenzione e sono stati inviati a un Manager, per le persone seguite dai FamilyTutor
 * selezionati, divisi per FamilyTutor. Il dettaglio di un caso (motivi, punteggi) si carica
 * solo quando si apre la modale (`LongevityAssessmentModalComponent`, la stessa di "Vedi risposte" nel
 * Registro della richiesta): aprire un test con un caso finisce nel log degli accessi.
 */
@Component({
  selector: 'app-longevity-alerts',
  templateUrl: './longevity-alerts.component.html',
  styleUrls: ['./longevity-alerts.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, LongevityAssessmentModalComponent],
})
export class LongevityAlertsComponent implements OnChanges, OnDestroy {
  /** FamilyTutor selezionati nella pagina Avvisi. Va riassegnato (nuovo array) a ogni cambio. */
  @Input() operators: LongevityOperatorTarget[] = [];

  readonly pageSize = 50;
  readonly statuses: { id: ManagerCaseStatus; label: string }[] = (
    ['OPEN', 'TAKEN_IN_CHARGE', 'CLOSED'] as ManagerCaseStatus[]
  ).map((id) => ({ id, label: MANAGER_CASE_STATUS_LABELS[id] }));
  readonly sortOptions: { id: LongevityCaseSort; label: string }[] = [
    { id: 'date-desc', label: 'Apertura: dal più recente' },
    { id: 'date-asc', label: 'Apertura: dal meno recente' },
    { id: 'status', label: 'Stato: aperti prima' },
  ];

  filters: LongevityCaseFilters = { ...EMPTY_LONGEVITY_CASE_FILTERS };
  isLoading = false;
  /** FamilyTutor per cui il caricamento è fallito: gli altri vengono comunque mostrati. */
  failedOperators: string[] = [];
  groups: LongevityCaseGroup[] = [];
  /** Casi caricati prima dei filtri. */
  loadedCount = 0;
  /** Casi rimasti dopo i filtri. */
  matchingCount = 0;

  /** Caso aperto nella modale, null quando è chiusa. */
  selectedCase: LongevityManagerCase | null = null;
  /** Caso con un'azione (prendi in carico/chiudi) in corso, per disabilitare i pulsanti. */
  actionPendingId: string | null = null;

  private allCases: LongevityManagerCase[] = [];
  private visible = new Map<string, number>();
  private cancelLoad = new Subject<void>();
  private unsubscribe = new Subject<void>();

  constructor(private service: LongevityAlertsService, private toast: ToastService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['operators']) {
      this.load();
    }
  }

  get activeFilters(): number {
    return activeFilterCount(this.filters);
  }

  load(): void {
    this.cancelLoad.next(); // annulla l'eventuale caricamento precedente ancora in volo
    this.failedOperators = [];
    this.closeDetail();

    if (this.operators.length === 0) {
      this.isLoading = false;
      this.allCases = [];
      this.afterLoad();
      return;
    }

    this.isLoading = true;
    forkJoin(
      this.operators.map((operator) =>
        this.findCasesOf(operator).pipe(
          map((rows) => ({ operator, rows, failed: false })),
          catchError((err) => {
            console.error(`Longevity: caricamento fallito per ${operator.name}`, err);
            return of({ operator, rows: [] as LongevityManagerCase[], failed: true });
          })
        )
      )
    )
      .pipe(takeUntil(this.cancelLoad), takeUntil(this.unsubscribe))
      .subscribe((results) => {
        // lib es2018: niente flatMap
        this.allCases = results.reduce<LongevityManagerCase[]>(
          (all, result) => all.concat(result.rows),
          []
        );
        this.failedOperators = results
          .filter((result) => result.failed)
          .map((result) => result.operator.name);
        this.isLoading = false;
        this.afterLoad();
      });
  }

  applyFilters(): void {
    this.visible.clear();
    const filtered = sortCases(filterCases(this.allCases, this.filters), this.filters.sort);
    this.matchingCount = filtered.length;
    this.groups = groupCasesByOperator(
      filtered,
      this.operators.map((operator) => operator.name)
    );
  }

  /** Lo stato è filtrato dal backend (query param `status`): va ricaricata la coda. */
  onStatusChange(): void {
    this.load();
  }

  resetFilters(): void {
    const statusChanged = this.filters.status !== '';
    this.filters = { ...EMPTY_LONGEVITY_CASE_FILTERS, sort: this.filters.sort };
    if (statusChanged) {
      this.load();
    } else {
      this.applyFilters();
    }
  }

  visibleRows(group: LongevityCaseGroup): LongevityManagerCase[] {
    return group.rows.slice(0, this.visible.get(group.operator) ?? this.pageSize);
  }

  hiddenCount(group: LongevityCaseGroup): number {
    return Math.max(group.rows.length - this.visibleRows(group).length, 0);
  }

  showMore(group: LongevityCaseGroup): void {
    this.visible.set(
      group.operator,
      (this.visible.get(group.operator) ?? this.pageSize) + this.pageSize
    );
  }

  trackByCase(_: number, row: LongevityManagerCase): string {
    return row.id;
  }

  statusLabel(status: ManagerCaseStatus): string {
    return MANAGER_CASE_STATUS_LABELS[status];
  }

  statusClass(status: ManagerCaseStatus): string {
    return `status-${status.toLowerCase().replace(/_/g, '-')}`;
  }

  openDetail(row: LongevityManagerCase): void {
    this.selectedCase = row;
  }

  closeDetail(): void {
    this.selectedCase = null;
  }

  /**
   * Il backend non fa passare un caso da OPEN a CLOSED (409): un caso aperto si prende prima in carico e,
   * solo a risposta arrivata, si chiude. Se la seconda chiamata fallisce la riga resta "Preso in carico",
   * come sul server, e un nuovo "Chiudi" fa la sola chiusura.
   */
  closeCase(row: LongevityManagerCase): void {
    const close$ =
      row.status === 'OPEN'
        ? this.service.takeInCharge(row.id).pipe(
            tap((detail) => this.mergeCaseUpdate(row, detail)),
            switchMap(() => this.service.close(row.id))
          )
        : this.service.close(row.id);
    this.runCaseAction(row, close$, 'Caso chiuso', 'Impossibile chiudere il caso');
  }

  /** Il PATCH risponde con il dettaglio già aggiornato: si riporta sulla riga e nella cache del dettaglio. */
  private runCaseAction(
    row: LongevityManagerCase,
    action: Observable<LongevityAssessmentDetail>,
    successMessage: string,
    errorMessage: string
  ): void {
    this.actionPendingId = row.id;
    action
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.actionPendingId = null))
      )
      .subscribe({
        next: (detail) => {
          this.mergeCaseUpdate(row, detail);
          this.toast.setAction(successMessage, true);
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 409) {
            this.reloadAfterConflict(row);
          } else {
            this.toast.setAction(errorMessage, false);
          }
        },
      });
  }

  /**
   * 409 INVALID_STATE: un altro operatore ha già cambiato il caso (o lo stato non permette il passaggio).
   * Si ricarica il test e si mostra com'è adesso.
   */
  private reloadAfterConflict(row: LongevityManagerCase): void {
    this.toast.setAction('Il caso è stato aggiornato da un altro operatore: ho ricaricato lo stato', false);
    this.service
      .getAssessmentDetail(row.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (detail) => this.mergeCaseUpdate(row, detail),
        error: (err) => console.error(`Longevity: ricaricamento del caso ${row.id} non riuscito`, err),
      });
  }

  private mergeCaseUpdate(row: LongevityManagerCase, detail: LongevityAssessmentDetail): void {
    const info = detail.caseInfo;
    if (info) {
      row.status = info.status;
      row.createdAt = info.createdAt;
      row.takenInChargeAt = info.takenInChargeAt;
      row.closedAt = info.closedAt;
      row.takenInChargeByYou = info.takenInChargeByYou;
    }
    this.applyFilters();
  }

  /** I casi di un FamilyTutor: serve il suo uid, senza il quale il caricamento conta come fallito. */
  private findCasesOf(operator: LongevityOperatorTarget): Observable<LongevityManagerCase[]> {
    return operator.uid
      ? this.service.findCases(operator.uid, operator.name, this.filters.status)
      : throwError(() => new Error(`uid non noto per ${operator.name}`));
  }

  private afterLoad(): void {
    this.loadedCount = this.allCases.length;
    this.applyFilters();
  }

  ngOnDestroy(): void {
    this.cancelLoad.next();
    this.cancelLoad.complete();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
